import { featureFlags } from '@seen/core/featureFlags'
import type { JsonObject } from '@seen/core/json'
import { ExtendedAllergy } from '@seen/core/models/allergies'
import { getReferenceString, referenceStringToId } from '@seen/core/references'
import { InsertAllergiesButtonNode as InsertAllergiesButtonSchema } from '@seen/core/tiptap/schema'
import { Button } from '@seen/ui'
import { IconLoader2, IconVirus } from '@tabler/icons-react'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { orderBy } from 'lodash-es'
import { type JSX, memo, useCallback, useState } from 'react'
import { toast } from 'sonner'
import {
  noteButtonClass,
  noteButtonLabelClass,
} from '#src/components/rich-text-editor/noteButtonClass.ts'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export const InsertAllergiesButtonNode = InsertAllergiesButtonSchema.extend({
  addNodeView() {
    return ReactNodeViewRenderer(InsertAllergiesButtonNodeComponent)
  },
})

function InsertAllergiesButtonNodeComponent(props: NodeViewProps): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <InsertAllergiesButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const InsertAllergiesButtonNodeComponentInner = memo(
  function InsertAllergiesButtonNodeComponentInner(
    props: NodeViewProps
  ): JSX.Element {
    const medplum = useMedplum()
    const participantReference = useOptionalParticipantReference()
    const getPosRef = useCurrentRef(props.getPos)
    const [isLoading, setIsLoading] = useState(false)
    const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)

    const handleClick = useCallback(async () => {
      if (!participantReference) {
        toast.error('No participant context available')
        return
      }

      setIsLoading(true)

      try {
        const allergies = await medplum.searchResourceClass(
          ExtendedAllergy,
          ExtendedAllergy.searchQuery({
            participant: participantReference,
          })
        )

        const sortedAllergies = orderBy(
          allergies,
          (a) => (a.onsetDateTime ? new Date(a.onsetDateTime).getTime() : 0),
          ['desc']
        )

        const allergyItems = sortedAllergies.filter((a) => a.type === 'allergy')
        const intoleranceItems = sortedAllergies.filter(
          (a) => a.type === 'intolerance'
        )

        const pos = getPosRef.current()
        if (pos === undefined) {
          return
        }
        props.deleteNode()

        if (allergyItems.length === 0 && intoleranceItems.length === 0) {
          props.editor
            .chain()
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text: 'No current allergies',
                },
              ],
            })
            .focus()
            .run()
          return
        }

        const patientId = referenceStringToId(
          getReferenceString(participantReference)
        )

        const content: JsonObject[] = []

        if (allergyItems.length > 0) {
          content.push({
            type: 'paragraph',
            content: [
              {
                type: 'text',
                marks: [{ type: 'bold' }],
                text: 'Allergy',
              },
            ],
          })
          content.push({
            type: 'bulletList',
            content: allergyItems.map((allergy) => ({
              type: 'listItem',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      marks: [
                        {
                          type: 'link',
                          attrs: {
                            href: `/Patient/${patientId}/allergies/${allergy.id}`,
                          },
                        },
                      ],
                      text: allergy.name,
                    },
                  ],
                },
              ],
            })),
          })
        }

        if (intoleranceItems.length > 0) {
          content.push({
            type: 'paragraph',
            content: [
              {
                type: 'text',
                marks: [{ type: 'bold' }],
                text: 'Intolerance',
              },
            ],
          })
          content.push({
            type: 'bulletList',
            content: intoleranceItems.map((allergy) => ({
              type: 'listItem',
              content: [
                {
                  type: 'paragraph',
                  content: [
                    {
                      type: 'text',
                      marks: [
                        {
                          type: 'link',
                          attrs: {
                            href: `/Patient/${patientId}/allergies/${allergy.id}`,
                          },
                        },
                      ],
                      text: allergy.name,
                    },
                  ],
                },
              ],
            })),
          })
        }

        props.editor.chain().insertContentAt(pos, content).focus().run()
      } catch (_error) {
        toast.error('Failed to fetch allergies')
        setIsLoading(false)
      }
    }, [medplum, participantReference, getPosRef, props])

    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className={noteButtonClass(inlineCapture)}
        disabled={!props.editor.isEditable || isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconLoader2 className="size-3.5 shrink-0 animate-spin text-foreground-secondary" />
        ) : (
          <IconVirus className="size-3.5 shrink-0 text-foreground-secondary" />
        )}
        <span className={noteButtonLabelClass(inlineCapture)}>
          Insert Current Allergies
        </span>
      </Button>
    )
  }
)
